import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import getOktaAuth from '@src/services/oktaService';
import { lookupIdp } from '@src/apis/reware';
import { AuthLayout } from '../AuthLayout';

/*
 * /sso page — single sign-on. Styled to match /login via the shared
 * <AuthLayout> shell; copy is SSO-specific. Takes a work email, looks up the
 * matching identity provider, and hands off to Okta. Auth wiring is unchanged.
 */
export const OktaLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your work email address.');
      return;
    }

    setLoading(true);

    try {
      const idpResult = await lookupIdp(trimmedEmail);
      const oktaAuth = getOktaAuth();

      const signInOptions: Record<string, string> = {};
      if (idpResult) {
        signInOptions.idp = idpResult.idpId;
      }

      await oktaAuth.signInWithRedirect(signInOptions);
    } catch (err) {
      console.error('SSO redirect failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to start single sign-on';
      setError(message);
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      heading="Single sign-on"
      subcopy="Enter your work email and we'll continue to your organization's identity provider."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Work email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={loading}
          autoFocus
          autoComplete="email"
          sx={{ mb: 1.5 }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ height: 44, fontSize: 15 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Continue with SSO'}
        </Button>
      </Box>

      <Divider sx={{ my: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
          or
        </Typography>
      </Divider>

      <Button fullWidth variant="outlined" sx={{ height: 44 }} onClick={() => navigate('/login')}>
        Sign in with a badge instead
      </Button>
    </AuthLayout>
  );
};
